import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { gsap } from 'gsap';
import { APP_URL } from '../../config';
import Icon from '../shared/Icon';

export default function PVHero() {
  const { t } = useTranslation();
  const rootRef = useRef(null);
  const bgRef = useRef(null);

  useEffect(() => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const scope = rootRef.current;
    if (!scope) return;

    const ctx = gsap.context(() => {
      if (!reduceMotion) {
        gsap.from(scope.querySelectorAll('[data-hero-reveal]'), {
          opacity: 0,
          y: 22,
          duration: 0.85,
          ease: 'power2.out',
          stagger: 0.08,
          delay: 0.15,
        });
      }

      // Subtle background parallax — tasteful, not cheesy.
      const onScroll = () => {
        const y = window.scrollY;
        if (bgRef.current) {
          bgRef.current.style.transform = `translate3d(0, ${y * 0.12}px, 0)`;
        }
      };
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
      return () => window.removeEventListener('scroll', onScroll);
    }, scope);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={rootRef}
      style={{
        position: 'relative',
        paddingTop: 64 + 96,
        paddingBottom: 96,
        overflow: 'hidden',
        borderBottom: '1px solid var(--p2a-dark-border)',
      }}
    >
      {/* Background image + gradient overlay — parallaxed behind content */}
      <div
        ref={bgRef}
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: '-10% 0 -10% 0',
          background:
            'linear-gradient(180deg, rgba(5,7,10,0.55) 0%, rgba(5,7,10,0.8) 100%), ' +
            'url("/assets/pixviewer_gaps_overview.webp") center/cover',
          zIndex: 0,
          willChange: 'transform',
        }}
      />
      <div className="grid-bg" style={{ position: 'absolute', inset: 0, opacity: 0.3, zIndex: 1 }} />

      <div className="p2-container" style={{ position: 'relative', zIndex: 2 }}>
        <div style={{ maxWidth: 780 }}>
          <div className="eyebrow" data-hero-reveal style={{ marginBottom: 20 }}>
            <span className="dot" />{t('pv.hero.eyebrow')}
          </div>
          <h1 className="h-display" data-hero-reveal>
            {t('pv.hero.title_line1')} <span style={{ color: 'var(--p2a-green-400)' }}>{t('pv.hero.title_line2_highlight')}</span>
          </h1>
          <p data-hero-reveal style={{ marginTop: 24, fontSize: 18, lineHeight: 1.55, color: 'var(--p2a-dark-fg-2)', maxWidth: 600 }}>
            {t('pv.hero.lede')}
          </p>
          <div data-hero-reveal style={{ display: 'flex', gap: 12, marginTop: 32, flexWrap: 'wrap' }}>
            <a href={APP_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
              {t('pv.hero.cta_primary')} <Icon.External />
            </a>
            <a href="#contato" className="btn btn-ghost">
              {t('pv.hero.cta_secondary')} <Icon.ArrowRight />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
